import { createContext, useContext } from 'react';
import type { CommandKind, Pitch } from '@drama/domain';
import type { ProjectDetail } from '../../../../packages/domain/src/view';
import type { episodeAllowance } from '../../../../packages/domain/src/production-plan';
import type { ProviderProfile } from '../../../../packages/domain/src/provider-profiles';

/**
 * Shared state for the series screen.
 *
 * The container computes roughly twenty derived values that most of the five tabs need.
 * Passing them individually would give each tab an unreadable signature, and recomputing
 * them per tab would risk the tabs disagreeing about, for example, whether controls should
 * be disabled. A context keeps one source of truth without the prop plumbing.
 */
export interface SeriesValue {
  detail: ProjectDetail;
  project: ProjectDetail['project'];
  /** Latest pitch bible, if the idea has been developed yet. */
  pitch: Pitch | undefined;
  /** Episode currently selected, defaulting to the first. */
  episode: ProjectDetail['episodes'][number] | undefined;
  /** Shots for the selected episode at its current script version, in order. */
  shots: ProjectDetail['shots'];
  plan: ReturnType<typeof episodeAllowance>;
  /** True when any command is still in flight. */
  running: boolean;
  /** True when the signed-in member may edit rather than only review. */
  writable: boolean;
  /** `busy || running || !writable` — the guard nearly every control uses. */
  controlsDisabled: boolean;
  busy: boolean;
  /** Remaining spend, respecting both the global cap and any trial cap. */
  available: number;
  model: string;
  effectiveModel: string;
  providerProfile: ProviderProfile | undefined;
  setModel: (value: string) => void;
  revision: string;
  setRevision: (value: string) => void;
  selectedShot: string | undefined;
  setSelectedShot: (value: string | undefined) => void;
  setSelectedEpisode: (value: string) => void;
  setTab: (value: string) => void;
  narration: string;
  setNarration: (value: string) => void;
  direction: string;
  setDirection: (value: string) => void;
  /** Submits a command for this project and clears the revision box on success. */
  run: (kind: CommandKind, extra?: Record<string, string | number | boolean>) => Promise<boolean>;
  /** Runs an arbitrary action with the shared busy flag and error reporting. */
  act: (action: () => Promise<unknown>) => Promise<void>;
  onError: (error: unknown) => void;
}

const Context = createContext<SeriesValue | null>(null);

export const SeriesProvider = Context.Provider;

export function useSeries() {
  const value = useContext(Context);
  if (!value) throw new Error('useSeries must be used inside the series screen.');
  return value;
}
